import type { FormEvent, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate, useSearchParams } from 'react-router';
import { BackLink, Button, ButtonGroup, ErrorSummary, RequiredFieldsNote, type ErrorSummaryItem } from '../../../components/idsk';
import { PageHeading } from '../../../layout/PageHeading';
import { usePageTitle } from '../../../layout/usePageTitle';
import { useWizard } from './WizardContext';
import { firstInvalidStep } from './validation';

export const STEP_PATHS = ['nazov-a-typ', 'tim', 'termin', 'suhrn'] as const;
export const WIZARD_BASE = '/inspekcie/nova';
export const TOTAL_STEPS = 4;

export function stepPath(step: number, change = false, base = WIZARD_BASE): string {
  return `${base}/${STEP_PATHS[step - 1]}${change ? '?zmena=1' : ''}`;
}

/** Step routes relative to the wizard's base (create or edit). */
export function useStepPath() {
  const { basePath } = useWizard();
  return (step: number, change = false) => stepPath(step, change, basePath);
}

/** Question-page layout of one wizard step: back link, error summary, caption "Krok N z 4", h1 and the step form. */
export function WizardStep({
  step,
  title,
  errors,
  submitCount,
  onContinue,
  children,
  before,
  continueText,
}: {
  step: number;
  title: string;
  errors: ErrorSummaryItem[];
  submitCount: number;
  /** Validates the step; returns true when it may continue. */
  onContinue?: () => boolean;
  children: ReactNode;
  /** Content outside the step form (e.g. the inline "new inspector" form, which must not be nested). */
  before?: ReactNode;
  continueText?: string;
}) {
  const { t } = useTranslation();
  const { state, reset, editing } = useWizard();
  const path = useStepPath();
  const navigate = useNavigate();
  const exit = editing ? `/inspekcie/${editing.id}` : '/inspekcie';
  const [params] = useSearchParams();
  const changing = params.get('zmena') === '1';
  const caption = t('wizard.stepOf', { step, total: TOTAL_STEPS });
  usePageTitle([caption, title, editing ? t('wizard.editTitle', { number: editing.number }) : t('wizard.title')]);

  // Users cannot jump forward past an invalid step.
  const firstInvalid = firstInvalidStep(state);
  if (firstInvalid < step) return <Navigate to={path(firstInvalid)} replace />;

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!onContinue || !onContinue()) return;
    const next = firstInvalidStep({ ...state });
    void navigate(changing && next === 4 ? path(4) : path(step + 1));
  };

  return (
    <>
      <BackLink to={step === 1 ? exit : path(step - 1)} />
      <div className="govuk-grid-row">
        <div className="govuk-grid-column-two-thirds">
          {errors.length > 0 && <ErrorSummary errors={errors} focusKey={submitCount} />}
          <PageHeading caption={caption}>{title}</PageHeading>
          {/* every step but the summary asks for required answers */}
          {step < TOTAL_STEPS && <RequiredFieldsNote />}
          {before}
          <form onSubmit={submit} noValidate>
            {children}
            {onContinue && (
              <ButtonGroup>
                <Button type="submit">{continueText ?? t('common.continue')}</Button>
                <Button
                  variant="secondary"
                  onClick={() => {
                    reset();
                    void navigate(exit);
                  }}
                >
                  {t('wizard.cancel')}
                </Button>
              </ButtonGroup>
            )}
          </form>
        </div>
      </div>
    </>
  );
}
